import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as executionApi from '../../services/execution-api.js';
import type { TimelinePayload } from '../../services/execution-api.js';
import { wsClient } from '../../services/websocket-client.js';

@customElement('cp-execution-status-badge')
export class CpExecutionStatusBadge extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) ticketId = '';
  @state() private _timeline: TimelinePayload | null = null;
  @state() private _elapsedSec = 0;

  private _wsHandler: ((e: Event) => void) | null = null;
  private _interval: ReturnType<typeof setInterval> | null = null;

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await this._fetchTimeline();
    this._startElapsedTimer();

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload: Record<string, unknown> }>).detail;
      if (!msg.payload || (msg.payload as { ticketId?: string }).ticketId !== this.ticketId) return;

      if (msg.type === 'execution:warning') {
        if (this._timeline) {
          this._timeline = { ...this._timeline, warningEmittedAt: (msg.payload.warningEmittedAt as string) ?? null };
        }
      } else if (msg.type === 'execution:first-output') {
        if (this._timeline) {
          this._timeline = { ...this._timeline, firstOutputAt: (msg.payload.firstOutputAt as string) ?? null };
        }
      } else if (msg.type === 'execution:timeline-updated') {
        void this._fetchTimeline();
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
    this._stopElapsedTimer();
  }

  private async _fetchTimeline(): Promise<void> {
    try {
      this._timeline = await executionApi.getExecutionTimeline(this.ticketId);
      this._updateElapsed();
    } catch {
      this._timeline = null;
    }
  }

  private _startElapsedTimer(): void {
    this._stopElapsedTimer();
    this._interval = setInterval(() => this._updateElapsed(), 1000);
  }

  private _stopElapsedTimer(): void {
    if (this._interval !== null) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  private _updateElapsed(): void {
    const t = this._timeline;
    if (!t?.startedAt) { this._elapsedSec = 0; return; }
    const end = t.completedAt ? new Date(t.completedAt).getTime() : Date.now();
    this._elapsedSec = Math.floor((end - new Date(t.startedAt).getTime()) / 1000);
  }

  private _formatElapsed(sec: number): string {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  }

  render() {
    const t = this._timeline;
    if (!t) return nothing;

    const isActive = t.status === 'running' || t.status === 'queued';
    const hasWarning = !!t.warningEmittedAt && !t.completedAt;

    return html`
      <span class="cp-execution-badge cp-execution-badge--${t.status}">
        <span class="cp-execution-badge__status">${t.status}</span>
        ${t.status === 'queued' && t.queuePosition !== null && t.queuePosition !== undefined
          ? html`<span class="cp-execution-badge__queue">#${t.queuePosition}</span>`
          : nothing}
        ${isActive
          ? html`<span class="cp-execution-badge__elapsed">${this._formatElapsed(this._elapsedSec)}</span>`
          : nothing}
        ${hasWarning
          ? html`<span class="cp-execution-badge__warning" title="Approaching timeout">⚠</span>`
          : nothing}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-execution-status-badge': CpExecutionStatusBadge;
  }
}
